import { Link } from "react-router-dom";
import { Clock, Phone, Users, CreditCard, Wifi, Accessibility, MapPin, Car, TramFront } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { site } from "../config/site";

// TODO: remplacer les photos par de vraies photos du restaurant (avec l'accord du client)
const PHOTOS = [
  { src: "https://images.unsplash.com/photo-1651440204216-548382747b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Salle chaleureuse du restaurant TN FOOD" },
  { src: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Couscous servi au restaurant TN FOOD" },
  { src: "https://images.unsplash.com/photo-1591299089616-c9604047b1a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", alt: "Thé à la menthe servi à la tunisienne" },
];

export default function Restaurant() {
  return (
    <main id="contenu-principal">
      <Seo
        title="Restaurant tunisien à Sainte-Savine — Cuisine traditionnelle | TN FOOD"
        description="Restaurant tunisien à Sainte-Savine près de Troyes : couscous, tajines, briks et grillades maison. Sur place, à emporter ou en livraison. 40 couverts + terrasse. Réservez au 07 75 77 29 51."
        path="/restaurant"
      />

      <section className="bg-bordeaux text-cream py-16 md:py-24" data-testid="restaurant-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal>
            <h1 className="font-display text-4xl md:text-5xl font-bold max-w-3xl">Le restaurant TN FOOD, la Tunisie à table à Sainte-Savine</h1>
            <p className="mt-6 text-lg text-cream/85 max-w-2xl leading-relaxed">
              Un cadre chaleureux et une ambiance conviviale pour découvrir la cuisine tunisienne traditionnelle : 40 couverts en salle, terrasse selon la saison, service sur place, à emporter et en livraison dans l'agglomération troyenne.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact#reservation" data-testid="restaurant-cta-reserver" className="rounded-full bg-saffron text-cream px-8 py-3 font-semibold transition-all duration-300 hover:bg-gold hover:-translate-y-0.5">Réserver une table</Link>
              <Link to="/carte" data-testid="restaurant-cta-carte" className="rounded-full border-2 border-cream text-cream px-8 py-3 font-semibold transition-colors hover:bg-cream hover:text-encre">Voir la carte</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl grid lg:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold text-encre">Horaires et réservation</h2>
            <ul className="mt-6 space-y-4 text-taupe">
              <li className="flex gap-3">
                <Clock className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span><strong className="text-encre">Mardi au dimanche :</strong> 11h30 – 14h30 et 18h30 – 22h30.<br />Fermé le lundi.</span>
              </li>
              <li className="flex gap-3">
                <Phone className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span>
                  Réservation par téléphone au <a href={site.phoneHref} data-testid="restaurant-phone" className="text-terracotta font-semibold hover:underline">{site.phone}</a> ou via notre <Link to="/contact#reservation" className="text-terracotta font-semibold hover:underline">formulaire de contact</Link>.
                  Réservations conseillées pour les groupes (à partir de 6 personnes) et le week-end.
                </span>
              </li>
              <li className="flex gap-3">
                <Users className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span><strong className="text-encre">Groupes et privatisation :</strong> possibilité de privatiser la salle pour vos événements familiaux ou professionnels — nous consulter.</span>
              </li>
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-display text-3xl font-semibold text-encre">Informations pratiques</h2>
            <ul className="mt-6 space-y-4 text-taupe">
              <li className="flex gap-3">
                <CreditCard className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span>{site.payment.restaurant}.</span>
              </li>
              <li className="flex gap-3">
                <Accessibility className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span>Établissement accessible aux personnes à mobilité réduite.</span>
              </li>
              <li className="flex gap-3">
                <Wifi className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span>Wifi gratuit.</span>
              </li>
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span>{site.address.full}</span>
              </li>
              <li className="flex gap-3">
                <Car className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span>Parking à proximité.</span>
              </li>
              <li className="flex gap-3">
                <TramFront className="h-5 w-5 mt-0.5 text-terracotta shrink-0" strokeWidth={1.5} />
                <span>Accessible par les transports de l'agglomération troyenne.</span>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 md:py-24" data-testid="restaurant-photos">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-10">
            <h2 className="font-display text-3xl font-semibold text-encre">L'ambiance TN FOOD</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PHOTOS.map((p, i) => (
              <Reveal key={p.src} delay={i * 0.1}>
                <img src={p.src} alt={p.alt} loading="lazy" width="900" height="600" className="rounded-2xl h-64 w-full object-cover shadow-sm" />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/contact#reservation" data-testid="restaurant-bottom-cta" className="inline-block rounded-full bg-terracotta text-cream px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5">
              Réserver une table
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
